import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  updateProfile,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  sendEmailVerification,
  sendPasswordResetEmail
} from 'firebase/auth';
import { auth, collectDeviceInfo, signInWithGoogle } from '../../config/firebase';

// Firebaseエラーメッセージの日本語化
const getJapaneseErrorMessage = (errorCode) => {
  switch (errorCode) {
    case 'auth/invalid-email':
      return 'メールアドレスの形式が正しくありません';
    case 'auth/user-disabled':
      return 'このアカウントは無効になっています';
    case 'auth/user-not-found':
      return 'アカウントが見つかりません';
    case 'auth/wrong-password':
      return 'パスワードが間違っています';
    case 'auth/email-already-in-use':
      return 'このメールアドレスは既に使用されています';
    case 'auth/operation-not-allowed':
      return 'この操作は許可されていません';
    case 'auth/weak-password':
      return 'パスワードが弱すぎます';
    default:
      return 'ログインに失敗しました。もう一度お試しください。';
  }
};

// メール認証の送信
export const sendVerificationEmail = createAsyncThunk(
  'auth/sendVerificationEmail',
  async (_, { rejectWithValue }) => {
    try {
      if (auth.currentUser) {
        await sendEmailVerification(auth.currentUser);
        return true;
      }
      return rejectWithValue('ユーザーが見つかりません');
    } catch (error) {
      return rejectWithValue('確認メールの送信に失敗しました');
    }
  }
);

// 新規ユーザー登録
export const registerUser = createAsyncThunk(
  'auth/register',
  async ({ email, password }, { dispatch, rejectWithValue }) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const deviceInfo = collectDeviceInfo();
      
      // メールアドレスからデフォルトのdisplayNameを生成
      const defaultDisplayName = email.split('@')[0];
      
      // Firebaseのプロフィールを更新
      await updateProfile(userCredential.user, {
        displayName: defaultDisplayName
      });
      
      // メール認証を送信
      await sendEmailVerification(userCredential.user);
      
      // メール認証モーダルを表示
      dispatch(setShowEmailVerificationModal(true));
      
      return {
        user: {
          uid: userCredential.user.uid,
          email: userCredential.user.email,
          displayName: defaultDisplayName,
          emailVerified: userCredential.user.emailVerified
        },
        deviceInfo
      };
    } catch (error) {
      return rejectWithValue(getJapaneseErrorMessage(error.code));
    }
  }
);

// メール/パスワードでログイン
export const loginWithEmail = createAsyncThunk(
  'auth/loginWithEmail',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const deviceInfo = collectDeviceInfo();
      
      return {
        user: {
          uid: userCredential.user.uid,
          email: userCredential.user.email,
        },
        deviceInfo
      };
    } catch (error) {
      return rejectWithValue(getJapaneseErrorMessage(error.code));
    }
  }
);

// Googleでログイン
export const loginWithGoogle = createAsyncThunk(
  'auth/loginWithGoogle',
  async (_, { rejectWithValue }) => {
    try {
      console.log('Googleログイン処理を開始...');
      const result = await signInWithGoogle();
      console.log('ログイン結果:', result);
      
      if (result && result.user) {
        const deviceInfo = await collectDeviceInfo();
        return {
          user: {
            uid: result.user.uid,
            email: result.user.email,
            displayName: result.user.displayName
          },
          deviceInfo
        };
      }
      return null;
    } catch (error) {
      console.error('Googleログインエラー:', error);
      return rejectWithValue(getJapaneseErrorMessage(error.code));
    }
  }
);

// ログアウト
export const signOut = createAsyncThunk(
  'auth/signOut',
  async (_, { rejectWithValue }) => {
    try {
      await firebaseSignOut(auth);
      return null;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const MAX_LOGIN_ATTEMPTS = 5;
const LOCKOUT_TIME = 15 * 60 * 1000; // 15分

const initialState = {
  user: null,
  deviceInfo: null,
  status: 'idle',
  error: null,
  showWelcomeModal: false,
  loginAttempts: 0,
  lockoutUntil: null,
  closeAuthDialog: false,
  showEmailVerificationModal: false,
  isResendingEmail: false
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload;
      // ログイン成功時にログイン試行回数をリセット
      state.loginAttempts = 0;
      state.lockoutUntil = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setShowWelcomeModal: (state, action) => {
      state.showWelcomeModal = action.payload;
    },
    incrementLoginAttempts: (state) => {
      state.loginAttempts += 1;
      if (state.loginAttempts >= MAX_LOGIN_ATTEMPTS) {
        state.lockoutUntil = Date.now() + LOCKOUT_TIME;
      }
    },
    checkLockoutStatus: (state) => {
      if (state.lockoutUntil && Date.now() >= state.lockoutUntil) {
        state.loginAttempts = 0;
        state.lockoutUntil = null;
      }
    },
    resetCloseAuthDialog: (state) => {
      state.closeAuthDialog = false;
    },
    setShowEmailVerificationModal: (state, action) => {
      state.showEmailVerificationModal = action.payload;
    },
    setIsResendingEmail: (state, action) => {
      state.isResendingEmail = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // 新規登録
      .addCase(registerUser.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.user = action.payload.user;
        state.deviceInfo = action.payload.deviceInfo;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      // メール/パスワードログイン
      .addCase(loginWithEmail.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loginWithEmail.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.user = action.payload.user;
        state.deviceInfo = action.payload.deviceInfo;
        state.showWelcomeModal = true;
        state.closeAuthDialog = true;
        // displayNameがない場合はメールアドレスから生成
        if (!state.user.displayName && state.user.email) {
          state.user.displayName = state.user.email.split('@')[0];
        }
      })
      .addCase(loginWithEmail.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
        // ログイン失敗時に試行回数を増やす
        state.loginAttempts += 1;
        if (state.loginAttempts >= MAX_LOGIN_ATTEMPTS) {
          state.lockoutUntil = Date.now() + LOCKOUT_TIME;
          state.error = `ログイン試行回数が上限を超えました。${Math.ceil(LOCKOUT_TIME / 60000)}分後に再試行してください。`;
        }
      })
      // Googleログイン
      .addCase(loginWithGoogle.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loginWithGoogle.fulfilled, (state, action) => {
        if (action.payload) {
          state.status = 'succeeded';
          state.user = action.payload.user;
          state.deviceInfo = action.payload.deviceInfo;
          state.showWelcomeModal = true;
          state.closeAuthDialog = true;
        }
      })
      .addCase(loginWithGoogle.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      // ログアウト
      .addCase(signOut.fulfilled, (state) => {
        state.user = null;
        state.deviceInfo = null;
        state.status = 'idle';
      })
      // プロフィール更新
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.user = action.payload;
      })
      // パスワード更新
      .addCase(updateUserPassword.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(updateUserPassword.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(updateUserPassword.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });
  }
});

// 全てのアクションをエクスポート
export const { 
  setUser, 
  clearError, 
  setShowWelcomeModal, 
  incrementLoginAttempts,
  checkLockoutStatus,
  resetCloseAuthDialog,
  setShowEmailVerificationModal,
  setIsResendingEmail
} = authSlice.actions;

// ユーザープロフィールの更新
export const updateUserProfile = createAsyncThunk(
  'auth/updateProfile',
  async ({ displayName }, { getState, rejectWithValue }) => {
    try {
      const { auth: { user } } = getState();
      await updateProfile(auth.currentUser, { displayName });
      
      return {
        ...user,
        displayName
      };
    } catch (error) {
      return rejectWithValue('プロフィールの更新に失敗しました');
    }
  }
);

// パスワードの更新
export const updateUserPassword = createAsyncThunk(
  'auth/updatePassword',
  async ({ currentPassword, newPassword }, { rejectWithValue }) => {
    try {
      const user = auth.currentUser;
      const credential = EmailAuthProvider.credential(
        user.email,
        currentPassword
      );
      
      // 現在のパスワードで再認証
      await reauthenticateWithCredential(user, credential);
      
      // パスワードの更新
      await updatePassword(user, newPassword);
      
      return null;
    } catch (error) {
      let errorMessage = 'パスワードの更新に失敗しました';
      if (error.code === 'auth/wrong-password') {
        errorMessage = '現在のパスワードが正しくありません';
      }
      return rejectWithValue(errorMessage);
    }
  }
);

// パスワードリセットメールの送信
export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async ({ email }, { rejectWithValue }) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return { email };
    } catch (error) {
      if (error.code === 'auth/user-not-found') {
        return rejectWithValue('このメールアドレスは登録されていません');
      }
      return rejectWithValue('パスワードリセットメールの送信に失敗しました');
    }
  }
);

// Reducerをエクスポート
export default authSlice.reducer;
